import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { domain } from './helpers';

const ThemeContext = createContext(null);

export const THEME_DEFAULTS = { accent: '#7c3aed', bg: '#0a0a0a', border: '#262626', text: '#f0f0f0' };

// ── Color math ──────────────────────────────────────────────────────────────

function hexToRgb(hex) {
    const n = parseInt(hex.replace('#', ''), 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function rgbToHsl(r, g, b) {
    r /= 255; g /= 255; b /= 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;
    if (max === min) { h = s = 0; }
    else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
            case g: h = ((b - r) / d + 2) / 6; break;
            default: h = ((r - g) / d + 4) / 6;
        }
    }
    return [h * 360, s * 100, l * 100];
}

function hslToHex(h, s, l) {
    h /= 360; s /= 100; l /= 100;
    const hue2rgb = (p, q, t) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1/6) return p + (q - p) * 6 * t;
        if (t < 1/2) return q;
        if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
        return p;
    };
    let r, g, b;
    if (s === 0) { r = g = b = l; }
    else {
        const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        const p = 2 * l - q;
        r = hue2rgb(p, q, h + 1/3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1/3);
    }
    return '#' + [r, g, b].map(x => Math.round(x * 255).toString(16).padStart(2, '0')).join('');
}

export function adjustL(hex, delta) {
    const [r, g, b] = hexToRgb(hex);
    const [h, s, l] = rgbToHsl(r, g, b);
    return hslToHex(h, s, Math.max(0, Math.min(95, l + delta * 100)));
}

function hexToRgba(hex, alpha) {
    const [r, g, b] = hexToRgb(hex);
    return `rgba(${r},${g},${b},${alpha})`;
}

// ── Token expansion ──────────────────────────────────────────────────────────
// Takes the 4 base colors and returns the full token set.
// The full set is what gets saved and applied — advanced mode lets users edit
// individual tokens within it without affecting the base derivation.

export function computeAllTokens({ accent, bg, border, text }) {
    return {
        accent,
        accentHover:    adjustL(accent, -0.15),
        heroBadgeText:  adjustL(accent,  0.30),
        bg,
        headerBg: adjustL(bg, 0.02),
        cardBg:   adjustL(bg, 0.04),
        surface:  adjustL(bg, 0.06),
        surface2: adjustL(bg, 0.10),
        surface3: adjustL(bg, 0.13),
        surface4: adjustL(bg, 0.16),
        border,
        borderSubtle: adjustL(border, -0.04),
        text,
        textMuted:   adjustL(text, -0.25),
        textSubtle:  adjustL(text, -0.40),
    };
}

// ── Theme application ────────────────────────────────────────────────────────
// Applies the full token set directly to :root — no derivation happens here.

export function applyTheme(tokens) {
    const root = document.documentElement;
    root.style.setProperty('--accent',            tokens.accent);
    root.style.setProperty('--accent-hover',      tokens.accentHover);
    root.style.setProperty('--hero-badge-text',   tokens.heroBadgeText ?? adjustL(tokens.accent, 0.30));
    root.style.setProperty('--accent-muted', hexToRgba(tokens.accent, 0.12)); // always derived (rgba)
    root.style.setProperty('--bg',           tokens.bg);
    root.style.setProperty('--header-bg',    tokens.headerBg);
    root.style.setProperty('--card-bg',      tokens.cardBg);
    root.style.setProperty('--surface',      tokens.surface);
    root.style.setProperty('--surface-2',    tokens.surface2);
    root.style.setProperty('--surface-3',    tokens.surface3);
    root.style.setProperty('--surface-4',    tokens.surface4);
    root.style.setProperty('--alt-translucent-bkgcolor', hexToRgba(tokens.bg, 0.85));
    root.style.setProperty('--border',        tokens.border);
    root.style.setProperty('--border-subtle', tokens.borderSubtle);
    root.style.setProperty('--text',          tokens.text);
    root.style.setProperty('--text-muted',    tokens.textMuted);
    root.style.setProperty('--text-subtle',   tokens.textSubtle);
}

// ── Provider ─────────────────────────────────────────────────────────────────

export function ThemeProvider({ children }) {
    const [theme, setThemeState] = useState(() => computeAllTokens(THEME_DEFAULTS));

    const setTheme = (next) => {
        // Accept either a full token set or a base-only set — expand if needed
        const full = 'surface' in next ? next : computeAllTokens({ ...THEME_DEFAULTS, ...next });
        setThemeState(full);
        applyTheme(full);
    };

    useEffect(() => {
        axios.get(`${domain}/api/content/theme`).then(res => {
            const row = Array.isArray(res.data) && res.data.find(r => r.section_key === 'colors');
            if (row?.body) {
                try {
                    const saved = JSON.parse(row.body);
                    // Expand if old save only has base keys; use as-is if full token set
                    const full = 'surface' in saved
                        ? { ...computeAllTokens(THEME_DEFAULTS), ...saved }
                        : computeAllTokens({ ...THEME_DEFAULTS, ...saved });
                    setThemeState(full);
                    applyTheme(full);
                } catch {}
            }
        }).catch(() => {});
    }, []);

    return <ThemeContext.Provider value={{ theme, setTheme }}>{children}</ThemeContext.Provider>;
}

export const useTheme = () => useContext(ThemeContext);
